import * as React from "react";

import "./template-upload-modal.scss";

import {flatten1}       from "@Common/utils/collections";
import {consumeContext} from "@Common/react/context";
import {ComponentLink,
        Link,
        makeChain}      from "@Common/react/cst2";
import {Load2}          from "@Common/react/load2";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {ModalService3}  from "@Common/ui-components/modal-service-3/modal-service-3";

import {ErrorMessage}                  from "./components/error-message";
import {BlankCanvasLayout}             from "./views/blank-canvas-layout";
import {BlankCanvasProduct}            from "./views/blank-canvas-product";
import {IdmlUploadLayout}              from "./views/idml-upload-layout";
import {IdmlUploadMissingFonts}        from "./views/idml-upload-missing-fonts";
import {IdmlUploadMissingLinks}        from "./views/idml-upload-missing-links";
import {IdmlUploadUnsupportedFeatures} from "./views/idml-upload-unsupported-features";
import {NewTemplateOptions}            from "./views/new-template-options";
import {TemplateCreationRouteLoad}     from "../template-creation-route/template-creation-route-load";
import {ModalLayout}                   from "../../../common/modal-layout/modal-layout";


export const TemplateUploadModal = ({
    activeTab,
    onError         = null,
    next: rootNext
}:{
    activeTab       : string,
    onError        ?: (message:string) => void,
    next            : ComponentLink<typeof ModalService3<{}>, "rootNext">,
}) => (

    makeChain<JSX.Element>()

    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("routerHistory"))
    .then(...consumeContext("userCreatedCatSubStyle"))
    .then(...consumeContext("systemConstants"))

    // set modal options
    .then("modal", ({routerHistory}, next:ComponentLink<typeof ModalService3<typeof TemplateUploadModal>>) => ModalService3({
        xdQaName: "Template Upload Modal",
        width: "fit-content",
        xToClose: !window.location.hash.startsWith("#new-template-upload-modal"),
        onPassiveClose: async () => {
            return !window.location.hash.startsWith("#new-template-upload-modal");
        },
        onClose: async () => {
            routerHistory.replace(`/manage-templates/${activeTab}`)
        },
        next,
        rootNext,
    }))

    // initialize state
    .then("state", ({}, next:ComponentLink<typeof UseState<{
        currentHash               : string,
        stepId                    : string,
        stepTitle                 : string,
        allowNavBack              : boolean,
        templateId                : number,
        isNewTemplate             : boolean,
        newTemplateId             : number,
        blankCanvasProductId      : number,
        blankCanvasProductName    : string,
        blankCanvasProductSubId   : number,
        uploadedTemplateLayoutId  : number,
        filter                    : string,
        idmlInfo                  : any,
        errorMessage              : string,
        acknowledgements          : string,
    }>>) => UseState({
        initValue: {
            currentHash: "",
            stepId: "",
            stepTitle: "New Template",
            allowNavBack: false,
            templateId: 0,
            isNewTemplate: false,
            newTemplateId: null,
            blankCanvasProductId: 0,
            blankCanvasProductName: "",
            blankCanvasProductSubId: 0,
            uploadedTemplateLayoutId: 0,
            filter: "",
            idmlInfo: null,
            errorMessage: "",
            acknowledgements: "",
        },
        next,
    }))

    // set options by hash
    .then(({state}, next:Link<JSX.Element|any>) => {
        if (state.value.currentHash === window.location.hash) return next();

        // set common reset elements when changing navigation paths
        const resetBlankCanvasValues = {
            blankCanvasProductId: 0,
            blankCanvasProductName: "",
            blankCanvasProductSubId: 0,
            filter: "",
        };
        const resetIdmlValues = {
            idmlInfo: null,
            uploadedTemplateLayoutId: 0,
        };

        // evaluate the hash to set state values
        let newValues = {};
        if (window.location.hash === "#new-template") {
            newValues = {
                ...resetBlankCanvasValues,
                ...resetIdmlValues,
                stepId: "NewTemplateOptions",
                stepTitle: "New Template",
                allowNavBack: false,
            };
        }
        else if (window.location.hash === "#new-template-product") {
            newValues = {
                ...resetBlankCanvasValues,
                ...resetIdmlValues,
                stepId: "BlankCanvasProduct",
                stepTitle: "Select Product",
                allowNavBack: true,
                errorMessage: "",
            };
        }
        else if (window.location.hash.startsWith("#new-template-layout-")) {
            const hashParts = window.location.hash.split("-");
            newValues = {
                ...resetIdmlValues,
                stepId: "BlankCanvasLayout",
                stepTitle: "Select " + state.value.blankCanvasProductName + " Layout",
                allowNavBack: true,
                blankCanvasProductId: parseInt(hashParts[4]) ?? 0,
                blankCanvasProductName: hashParts[3] ?? "",
                blankCanvasProductSubId: parseInt(hashParts[5]) ?? 0,
                filter: hashParts[6] ?? "",
                errorMessage: "",
            };
        }
        else if (window.location.hash === "#new-template-uploaded") {
            if (!state.value.idmlInfo) return window.location.hash = "#new-template";
            newValues = {
                ...resetBlankCanvasValues,
                stepId: "IdmlUploadLayout",
                stepTitle: "Select Layout",
                allowNavBack: true,
                errorMessage: "",
            };
        }
        else if (window.location.hash === "#new-template-upload-links") {
            if (!state.value.idmlInfo) return window.location.hash = "#new-template";
            if (!state.value.uploadedTemplateLayoutId) return window.location.hash = "#new-template-uploaded";
            if (!state.value.idmlInfo.links.length) return window.location.hash = "#new-template-review-features";
            newValues = {
                ...resetBlankCanvasValues,
                stepId: "IdmlUploadMissingLinks",
                stepTitle: "Upload Links",
                allowNavBack: true,
                errorMessage: "",
            };
        }
        else if (window.location.hash === "#new-template-review-features") {
            if (!state.value.idmlInfo) return window.location.hash = "#new-template";
            if (!state.value.uploadedTemplateLayoutId) return window.location.hash = "#new-template-uploaded";
            if (!state.value.idmlInfo.warnings.length) return window.location.hash = "#new-template-missing-fonts";
            newValues = {
                ...resetBlankCanvasValues,
                stepId: "IdmlUploadUnsupportedFeatures",
                stepTitle: "Review Features",
                allowNavBack: true,
                errorMessage: "",
            };
        }
        else if (window.location.hash === "#new-template-missing-fonts") {
            if (!state.value.idmlInfo) return window.location.hash = "#new-template";
            if (!state.value.uploadedTemplateLayoutId) return window.location.hash = "#new-template-uploaded";
            if (!state.value.idmlInfo.fonts.filter(x => !x.isInstalled)?.length) return window.location.hash = `#new-template-upload-modal-${state.value.uploadedTemplateLayoutId}-new`;
            newValues = {
                ...resetBlankCanvasValues,
                stepId: "IdmlUploadMissingFonts",
                stepTitle: "Missing Fonts",
                allowNavBack: true,
                errorMessage: "",
            };
        }
        else if (window.location.hash.startsWith("#new-template-upload-modal-")) {
            const hashParts = window.location.hash.split("-");
            newValues = {
                stepId: "TemplateCreationRouteLoad",
                stepTitle: "Loading Template Creator",
                allowNavBack: false,
                templateId: parseInt(hashParts[4]) ?? 0,
                isNewTemplate: hashParts[5] !== "update",
            };
        }

        // update the state
        state.onChange({
            ...state.value,
            currentHash: window.location.hash,
            ...newValues
        });

        return next();
    })

    // create products list
    .then("products", ({userCreatedCatSubStyle}, next:Link<JSX.Element, any[]>) => next(userCreatedCatSubStyle?.value ?? []))

    // load templates related to the selected product
    .then("productLayouts", ({state, apis}, next:ComponentLink<typeof Load2<any[]>>) => Load2({
        _key: state.value.blankCanvasProductSubId,
        fetch: async () => {
            if (!state.value.blankCanvasProductSubId) return false;

            const styleTemplates = await apis.next.getStyleTemplate([state.value.blankCanvasProductSubId]);
            return flatten1(
                Object.keys(styleTemplates)
                    .filter((styleId) => !state.value.filter || state.value.filter == styleId)
                    .map((styleId) => styleTemplates[styleId].templates)
            ).sort((x, y) => x.seq - y.seq);

        },
        captureException: true,
        next,
    }))

    // load templates of the same size as the uploaded IDML
    .then("idmlLayouts", ({state, apis, products}, next:ComponentLink<typeof Load2<any[]>>) => Load2({
        _key: `${state.value.idmlInfo?.pageSize?.width}x${state.value.idmlInfo?.pageSize?.height}`,
        fetch: async () => {
            if (!state.value.idmlInfo || !products) return false;

            const sizeTemplates = await apis.next.getSizeTemplate(state.value.idmlInfo.pageSize.width, state.value.idmlInfo.pageSize.height);
            const validCategoryIds = new Set(Object.keys(products).map(Number));

            for (const sizeTemplateId in sizeTemplates) {
                const sizeTemplate = sizeTemplates[sizeTemplateId];
                sizeTemplate.templates = sizeTemplate.templates.filter(template => validCategoryIds.has(template.categoryId));
                if (sizeTemplate.templates.length === 0) {
                    delete sizeTemplates[sizeTemplateId];
                }
            }

            return flatten1(
                Object.keys(sizeTemplates)
                    .filter((styleId) => !state.value.filter || state.value.filter == styleId)
                    .map((styleId) => sizeTemplates[styleId].templates)
            ).sort((x, y) => x.seq - y.seq);

        },
        captureException: true,
        next,
    }))

    // load templates with same size as the uploaded IDML, but rotated sideways
    .then("rotatedIdmlLayouts", ({state, apis, products}, next:ComponentLink<typeof Load2<any[]>>) => Load2({
        _key: `${state.value.idmlInfo?.pageSize?.width}x${state.value.idmlInfo?.pageSize?.height}`,
        fetch: async () => {
            if (!state.value.idmlInfo || !products) return false;

            const sizeTemplates = await apis.next.getSizeTemplate(state.value.idmlInfo.pageSize.height, state.value.idmlInfo.pageSize.width);
            const validCategoryIds = new Set(Object.keys(products).map(Number));

            for (const sizeTemplateId in sizeTemplates) {
                const sizeTemplate = sizeTemplates[sizeTemplateId];
                sizeTemplate.templates = sizeTemplate.templates.filter(template => validCategoryIds.has(template.categoryId));
                if (sizeTemplate.templates.length === 0) {
                    delete sizeTemplates[sizeTemplateId];
                }
            }

            return flatten1(
                Object.keys(sizeTemplates)
                    .filter((styleId) => !state.value.filter || state.value.filter == styleId)
                    .map((styleId) => sizeTemplates[styleId].templates)
            ).sort((x, y) => x.seq - y.seq);

        },
        captureException: true,
        next,
    }))
    .then("availableGuides", ({apis}, next) => Load2({
        fetch: async () => Object.values(await apis.next.guideFiles.listGuideFiles()),
        next,
    }))

    // render the modal contents
    .then(({state, products, idmlLayouts, rotatedIdmlLayouts, productLayouts, availableGuides, routerHistory, modal, apis}) => (
        <ModalLayout title={state.value.stepTitle}
            back={state.value.allowNavBack}
            content={
                <div className="template-upload-modal-s8c">
                    <ErrorMessage text={state.value.errorMessage}/>
                    {state.value.stepId === "TemplateCreationRouteLoad" ? (
                        <TemplateCreationRouteLoad
                            match={{
                                params: {
                                    source_id: state.value.isNewTemplate ? state.value.templateId : undefined,
                                    template_id: !state.value.isNewTemplate ? state.value.templateId : state.value.newTemplateId
                                }
                            }}
                            newTemplate={state.value.isNewTemplate}
                            acknowledgements={state.value.acknowledgements}
                            filePath={state.value.idmlInfo?.filePath}
                            onError={errorMessage => {
                                // if setting up a new template, show the error within the modal
                                if (state.value.isNewTemplate) {
                                    state.onChange({...state.value, errorMessage});
                                    window.location.hash = "#new-template";
                                }
                                // if editing a template, close the modal and show the error in the main UI
                                else {
                                    if (onError) onError(errorMessage);
                                    modal.close();
                                    routerHistory.replace(`/manage-templates/${activeTab}`);
                                }
                            }}
                        />
                    ) : state.value.stepId === "BlankCanvasProduct" ? (
                        <BlankCanvasProduct
                            products={products}
                        />
                    ) : state.value.stepId === "BlankCanvasLayout" ? (
                        <BlankCanvasLayout
                            layouts={productLayouts}
                        />
                    ) : state.value.stepId === "IdmlUploadLayout" ? (
                        <IdmlUploadLayout
                            idmlInfo={state.value.idmlInfo}
                            layouts={idmlLayouts}
                            rotatedLayouts={rotatedIdmlLayouts}
                            availableGuides={availableGuides}
                            onSelectLayout={async (layoutId, idmlInfo) => {
                                const missingFonts=state.value.idmlInfo.fonts.filter(x => !x.isInstalled);
                                const acknowledgements = JSON.stringify({
                                    acknowledgedFonts: missingFonts,
                                    acknowledgedWarnings: state.value.idmlInfo.warnings
                                });
                                var newTemplateId = null;
                                if(state.value.templateId === 0) {
                                    const result = await apis.next.userCreated.updateTemplate({
                                        updateFields: {
                                            name: idmlInfo.fileName,
                                            active: true,
                                            sourceTemplateId: layoutId,
                                        }
                                    });
                                    if (result?.errors || result?.errorMessage) {
                                        state.onChange({...state.value, errorMessage: result?.errorMessage});
                                    }
                                    newTemplateId = result.idPrimary;
                                }
                                state.onChange({...state.value, newTemplateId: newTemplateId, uploadedTemplateLayoutId: layoutId, acknowledgements: acknowledgements});
                                window.location.hash = "#new-template-upload-links";
                            }}
                            onUploadComplete={(idmlInfo) => {
                                state.onChange({...state.value, idmlInfo});
                                window.location.hash = "#new-template-uploaded";
                            }}
                            onUploadError={errorMessage => state.onChange({...state.value, errorMessage})}
                        />
                    ) : state.value.stepId === "IdmlUploadMissingLinks" ? (
                        <IdmlUploadMissingLinks
                            links={state.value.idmlInfo.links}
                            saveFolder={state.value.idmlInfo.folderPath}
                            onComplete={() => window.location.hash = "#new-template-review-features"}
                        />
                    ) : state.value.stepId === "IdmlUploadUnsupportedFeatures" ? (
                        <IdmlUploadUnsupportedFeatures
                            warnings={state.value.idmlInfo.warnings}
                            onComplete={() => window.location.hash = "#new-template-missing-fonts"}
                        />
                    ) : state.value.stepId === "IdmlUploadMissingFonts" ? (
                        <IdmlUploadMissingFonts
                            missingFonts={state.value.idmlInfo.fonts.filter(x => !x.isInstalled)}
                            onComplete={() => window.location.hash = `#new-template-upload-modal-${state.value.uploadedTemplateLayoutId}-new`}
                        />
                    ) : (
                        <NewTemplateOptions
                            onUploadComplete={(idmlInfo) => {
                                state.onChange({...state.value, idmlInfo});
                                window.location.hash = "#new-template-uploaded";
                            }}
                            onUploadError={errorMessage => state.onChange({...state.value, errorMessage})}
                        />
                    )}
                </div>
            }
        />
    ))

    .run()

);